import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./ProductDetail.module.scss";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Select,
  Button,
  Typography,
  Space,
  Skeleton,
  Empty,
  Alert,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ShoppingCartOutlined,
  TagOutlined,
  FlagOutlined,
  InboxOutlined,
  SyncOutlined,
  PieChartOutlined,
  AlertOutlined,
  ThunderboltOutlined,
  ShopOutlined,
  LineChartOutlined,
  LinkOutlined,
  TrophyOutlined,
  SwapOutlined,
  AppstoreOutlined,
  BulbOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as CTitle,
  Legend,
  Tooltip as CTooltip,
  Filler,
} from "chart.js";
import {
  getProductAnalysisHeader,
  getProductAnalysisKpis,
  getProductAnalysisDetailChart,
  getProductAnalysisDetailBreakdown,
  ProductAnalysisHeader,
  ProductAnalysisKpis,
  ProductAnalysisChart,
  ProductAnalysisBreakdown,
} from "../../../api/Services";
import { useFetchRequestGuard } from "../../../helpers/FilterSearchHelper";
import {
  isVariationAvailable,
  variationNotAvailableLabel,
} from "../../../helpers/VariationHelper";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  CTitle,
  Legend,
  CTooltip,
  Filler
);

const { Title, Text } = Typography;

const fmt = (v?: number) =>
  `$${(v ?? 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v?: number) => (v ?? 0).toLocaleString("es-AR");

const categoryTree = (categoryName?: string | null, parents?: string[]) => {
  const parentsString = (parents || []).filter(Boolean).join("/");
  if (!categoryName) return "";
  return parentsString ? `${parentsString}/${categoryName}` : categoryName;
};

const ALERT_I18N_KEYS: Record<string, string> = {
  NO_RECENT_SALES: "ProductAnalysis-Alert-NoRecentSales",
  SALES_DROP_VS_LY: "ProductAnalysis-Alert-SalesDropVsLY",
  MARGIN_DECLINE: "ProductAnalysis-Alert-MarginDecline",
  VARIANT_CONCENTRATION: "ProductAnalysis-Alert-VariantConcentration",
  PROMO_DEPENDENCY: "ProductAnalysis-Alert-PromoDependency",
  STOCKOUT_RISK: "ProductAnalysis-Alert-StockoutRisk",
  LOW_ROTATION_HIGH_STOCK: "ProductAnalysis-Alert-LowRotationHighStock",
  OVERSTOCK: "ProductAnalysis-Alert-Overstock",
};

const Vr: React.FC<{ value?: number | null; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (!isVariationAvailable(value)) return <span>{variationNotAvailableLabel()}</span>;
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}
    >
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const varCell = (val?: number, var_?: number, isCurrency = true) => (
  <div className={styles.cellStack}>
    <Text strong>{isCurrency ? fmt(val) : fmtN(val)}</Text>
    <Vr value={var_} />
  </div>
);

const ProductDetailHeaderSkeleton: React.FC = () => (
  <Card className={styles.headerCard}>
    <Skeleton.Input active style={{ width: 280, height: 28, marginBottom: 16 }} />
    <Skeleton active paragraph={{ rows: 2 }} />
  </Card>
);

const ProductDetailKpisSkeleton: React.FC = () => (
  <div className={styles.kpiRow}>
    {Array.from({ length: 6 }).map((_, i) => (
      <Card className={styles.kpiCard} key={i}>
        <Skeleton active paragraph={{ rows: 2, width: ["60%", "80%"] }} title={false} />
      </Card>
    ))}
  </div>
);

const ProductDetailChartSkeleton: React.FC = () => (
  <Card className={styles.sectionCard}>
    <Skeleton.Input active size="small" style={{ width: 200, height: 16, marginBottom: 16 }} />
    <Skeleton.Input active style={{ width: "100%", height: 220 }} />
  </Card>
);

const ProductDetailBreakdownSkeleton: React.FC = () => (
  <>
    <Card className={styles.sectionCard}>
      <Skeleton active paragraph={{ rows: 8 }} />
    </Card>
    <Card className={styles.sectionCard}>
      <Skeleton active paragraph={{ rows: 8 }} />
    </Card>
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={8}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      </Col>
    </Row>
    <Card className={styles.sectionCard}>
      <Skeleton active paragraph={{ rows: 6 }} />
    </Card>
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      </Col>
    </Row>
    <Card className={styles.sectionCard}>
      <Skeleton active paragraph={{ rows: 8 }} />
    </Card>
  </>
);

const ProductDetailSectionError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation();
  return (
    <Alert
      type="error"
      showIcon
      message={t("ProductAnalysis-LoadError")}
      action={
        <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
          {t("ProductAnalysis-Retry")}
        </Button>
      }
      style={{ marginBottom: 16 }}
    />
  );
};

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [period, setPeriod] = useState<number>(30);
  const [channelId, setChannelId] = useState<number | undefined>();
  const [branchOfficeId, setBranchOfficeId] = useState<number | undefined>();

  const [header, setHeader] = useState<ProductAnalysisHeader | null>(null);
  const [kpis, setKpis] = useState<ProductAnalysisKpis | null>(null);
  const [chart, setChart] = useState<ProductAnalysisChart | null>(null);
  const [breakdown, setBreakdown] = useState<ProductAnalysisBreakdown | null>(null);
  const [headerLoading, setHeaderLoading] = useState(true);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [breakdownLoading, setBreakdownLoading] = useState(true);
  const [kpisError, setKpisError] = useState(false);
  const [chartError, setChartError] = useState(false);
  const [breakdownError, setBreakdownError] = useState(false);

  // Header doesn't depend on period/channel/store (it's just the article's own data),
  // so it gets its own guard/effect keyed only on id — changing filters never re-fetches
  // or invalidates it.
  const { beginRequest: beginHeaderRequest, isStaleRequest: isStaleHeaderRequest } =
    useFetchRequestGuard();
  const { beginRequest: beginKpisRequest, isStaleRequest: isStaleKpisRequest } =
    useFetchRequestGuard();
  const { beginRequest: beginChartRequest, isStaleRequest: isStaleChartRequest } =
    useFetchRequestGuard();
  const { beginRequest: beginBreakdownRequest, isStaleRequest: isStaleBreakdownRequest } =
    useFetchRequestGuard();

  const fetchHeader = useCallback(() => {
    if (!id) return;
    const requestId = beginHeaderRequest();
    setHeaderLoading(true);
    getProductAnalysisHeader(Number(id)).then((result) => {
      if (isStaleHeaderRequest(requestId)) return;
      setHeader(result);
      setHeaderLoading(false);
    });
  }, [id, beginHeaderRequest, isStaleHeaderRequest]);

  useEffect(() => {
    fetchHeader();
  }, [fetchHeader]);

  const fetchKpis = useCallback(() => {
    if (!id) return;
    const requestId = beginKpisRequest();
    setKpisLoading(true);
    setKpisError(false);
    getProductAnalysisKpis(Number(id), { period, channelId, branchOfficeId }).then(
      (result) => {
        if (isStaleKpisRequest(requestId)) return;
        setKpis(result);
        setKpisError(!result);
        setKpisLoading(false);
      }
    );
  }, [id, period, channelId, branchOfficeId, beginKpisRequest, isStaleKpisRequest]);

  const fetchChart = useCallback(() => {
    if (!id) return;
    const requestId = beginChartRequest();
    setChartLoading(true);
    setChartError(false);
    getProductAnalysisDetailChart(Number(id), { period, channelId, branchOfficeId }).then(
      (result) => {
        if (isStaleChartRequest(requestId)) return;
        setChart(result);
        setChartError(!result);
        setChartLoading(false);
      }
    );
  }, [id, period, channelId, branchOfficeId, beginChartRequest, isStaleChartRequest]);

  const fetchBreakdown = useCallback(() => {
    if (!id) return;
    const requestId = beginBreakdownRequest();
    setBreakdownLoading(true);
    setBreakdownError(false);
    getProductAnalysisDetailBreakdown(Number(id), { period, channelId, branchOfficeId }).then(
      (result) => {
        if (isStaleBreakdownRequest(requestId)) return;
        setBreakdown(result);
        setBreakdownError(!result);
        setBreakdownLoading(false);
      }
    );
  }, [
    id,
    period,
    channelId,
    branchOfficeId,
    beginBreakdownRequest,
    isStaleBreakdownRequest,
  ]);

  useEffect(() => {
    fetchKpis();
    fetchChart();
    fetchBreakdown();
  }, [fetchKpis, fetchChart, fetchBreakdown]);

  // Non-nullable convenience view combining the three independently-loaded pieces —
  // each section reads from here but renders its own skeleton while its own flag is
  // true, so this never gates whether a section is shown (only its content).
  const data = useMemo(
    () => ({
      byChannel: breakdown?.byChannel ?? [],
      byStore: breakdown?.byStore ?? [],
      salesEvolution: chart?.salesEvolution ?? [],
      alerts: breakdown?.alerts ?? [],
      drivers: breakdown?.drivers ?? [],
      opportunities: breakdown?.opportunities ?? [],
      promotions: breakdown?.promotions ?? [],
      complementary: breakdown?.complementary ?? [],
      similar: breakdown?.similar ?? [],
      skus: breakdown?.skus ?? [],
      similarEnabled: breakdown?.similarEnabled ?? false,
    }),
    [chart, breakdown]
  );

  const channelOptions = useMemo(
    () =>
      data.byChannel.map((c) => ({
        value: c.channelId,
        label: c.channelName,
      })),
    [data]
  );

  const branchOptions = useMemo(
    () =>
      data.byStore.map((s) => ({
        value: s.branchOfficeId,
        label: s.branchOfficeName,
      })),
    [data]
  );

  const chartData = useMemo(() => {
    const points = data.salesEvolution;
    return {
      labels: points.map((p) => p.label),
      datasets: [
        {
          label: t("ProductDetail-Chart-Observed"),
          data: points.map((p) => p.salesObserved),
          borderColor: "#8b5cf6",
          backgroundColor: "rgba(139,92,246,0.08)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          borderWidth: 2,
          spanGaps: true,
        },
        {
          label: t("ProductDetail-Chart-Forecast"),
          data: points.map((p) => p.salesForecast),
          borderColor: "#faad14",
          borderDash: [6, 4],
          backgroundColor: "transparent",
          tension: 0.3,
          pointRadius: 3,
          borderWidth: 2,
          spanGaps: true,
        },
      ],
    };
  }, [data, t]);

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: { usePointStyle: true, font: { size: 12 } },
      },
    },
    scales: {
      y: { beginAtZero: false, grid: { color: "rgba(0,0,0,0.04)" } },
      x: { grid: { display: false } },
    },
  };

  const channelCols = [
    {
      title: t("ProductAnalysis-Column-Channel"),
      dataIndex: "channelName",
      key: "channelName",
    },
    {
      title: t("ProductAnalysis-Column-Presence"),
      dataIndex: "presence",
      key: "presence",
      render: (v: string) => (
        <Tag color={v === "Presente" ? "green" : "default"}>{v}</Tag>
      ),
    },
    {
      title: t("ProductAnalysis-Column-Stock"),
      dataIndex: "stock",
      key: "stock",
      render: (v: number) => fmtN(v),
      align: "right" as const,
    },
    {
      title: t("ProductAnalysis-Column-GrossSales"),
      key: "sales",
      align: "right" as const,
      render: (_: any, r: any) => varCell(r.grossSales, r.grossSalesVar),
    },
    {
      title: t("ProductAnalysis-Column-Units"),
      key: "units",
      align: "right" as const,
      render: (_: any, r: any) => varCell(r.units, r.unitsVar, false),
    },
    {
      title: t("ProductAnalysis-Column-Tickets"),
      key: "tickets",
      align: "right" as const,
      render: (_: any, r: any) => varCell(r.tickets, r.ticketsVar, false),
    },
    {
      title: t("ProductAnalysis-Column-AvgTicket"),
      key: "avgTicket",
      align: "right" as const,
      render: (_: any, r: any) => varCell(r.avgTicket, r.avgTicketVar),
    },
    {
      title: t("ProductAnalysis-Column-Margin"),
      key: "margin",
      align: "right" as const,
      render: (_: any, r: any) => (
        <div className={styles.cellStack}>
          <Text strong>{(r.marginPct ?? 0).toFixed(1)}%</Text>
          <Vr value={r.marginVar} suffix=" pp" />
        </div>
      ),
    },
    {
      title: t("ProductAnalysis-Column-PctChannelSales"),
      dataIndex: "pctChannelSales",
      key: "pctChannelSales",
      align: "right" as const,
      render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
    },
    {
      title: t("ProductAnalysis-Column-ForecastSales"),
      dataIndex: "forecastSales",
      key: "forecastSales",
      align: "right" as const,
      render: (v: number) => (v > 0 ? fmt(v) : "—"),
    },
  ];

  const storeCols = [
    {
      title: t("ProductAnalysis-Column-Store"),
      dataIndex: "branchOfficeName",
      key: "branchOfficeName",
      sorter: (a: any, b: any) =>
        (a.branchOfficeName ?? "").localeCompare(b.branchOfficeName ?? ""),
    },
    {
      title: t("ProductAnalysis-Column-Channel"),
      dataIndex: "channelName",
      key: "channelName",
      render: (v: string) => v || "—",
      sorter: (a: any, b: any) =>
        (a.channelName ?? "").localeCompare(b.channelName ?? ""),
    },
    {
      title: t("ProductAnalysis-Column-Presence"),
      dataIndex: "presence",
      key: "presence",
      render: (v: string) => (
        <Tag color={v === "Presente" ? "green" : "default"}>{v}</Tag>
      ),
      sorter: (a: any, b: any) =>
        (a.presence ?? "").localeCompare(b.presence ?? ""),
    },
    {
      title: t("ProductAnalysis-Column-Stock"),
      dataIndex: "stock",
      key: "stock",
      render: (v: number) => fmtN(v),
      align: "right" as const,
      sorter: (a: any, b: any) => (a.stock ?? 0) - (b.stock ?? 0),
    },
    {
      title: t("ProductAnalysis-Column-GrossSales"),
      key: "sales",
      align: "right" as const,
      render: (_: any, r: any) => varCell(r.grossSales, r.grossSalesVar),
      sorter: (a: any, b: any) => (a.grossSales ?? 0) - (b.grossSales ?? 0),
    },
    {
      title: t("ProductAnalysis-Column-Units"),
      key: "units",
      align: "right" as const,
      render: (_: any, r: any) => varCell(r.units, r.unitsVar, false),
      sorter: (a: any, b: any) => (a.units ?? 0) - (b.units ?? 0),
    },
    {
      title: t("ProductAnalysis-Column-Tickets"),
      key: "tickets",
      align: "right" as const,
      render: (_: any, r: any) => varCell(r.tickets, r.ticketsVar, false),
      sorter: (a: any, b: any) => (a.tickets ?? 0) - (b.tickets ?? 0),
    },
    {
      title: t("ProductAnalysis-Column-AvgTicket"),
      key: "avgTicket",
      align: "right" as const,
      render: (_: any, r: any) => varCell(r.avgTicket, r.avgTicketVar),
      sorter: (a: any, b: any) => (a.avgTicket ?? 0) - (b.avgTicket ?? 0),
    },
    {
      title: t("ProductAnalysis-Column-Margin"),
      key: "margin",
      align: "right" as const,
      render: (_: any, r: any) => (
        <div className={styles.cellStack}>
          <Text strong>{(r.marginPct ?? 0).toFixed(1)}%</Text>
          <Vr value={r.marginVar} suffix=" pp" />
        </div>
      ),
      sorter: (a: any, b: any) => (a.marginPct ?? 0) - (b.marginPct ?? 0),
    },
    {
      title: t("ProductAnalysis-Column-PctStoreSales"),
      dataIndex: "pctStoreSales",
      key: "pctStoreSales",
      align: "right" as const,
      render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
      sorter: (a: any, b: any) =>
        (a.pctStoreSales ?? 0) - (b.pctStoreSales ?? 0),
    },
    {
      title: t("ProductAnalysis-Column-ForecastSales"),
      dataIndex: "forecastSales",
      key: "forecastSales",
      align: "right" as const,
      render: (v: number) => (v > 0 ? fmt(v) : "—"),
      sorter: (a: any, b: any) =>
        (a.forecastSales ?? 0) - (b.forecastSales ?? 0),
    },
  ];

  if (!headerLoading && !header) {
    return (
      <div className={styles.wrapper}>
        <Button
          icon={<ArrowLeftOutlined />}
          type="text"
          className={styles.backBtn}
          onClick={() => navigate("/commercial-analysis/products")}
        >
          {t("ProductAnalysis-Title")}
        </Button>
        <Empty description={t("ProductAnalysis-Empty-NoProductFound")} />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Button
        icon={<ArrowLeftOutlined />}
        type="text"
        className={styles.backBtn}
        onClick={() => navigate("/commercial-analysis/products")}
      >
        {t("ProductAnalysis-Title")}
      </Button>

      {headerLoading ? (
        <ProductDetailHeaderSkeleton />
      ) : (
        header && (
          <Card className={styles.headerCard}>
            <div className={styles.headerRow}>
              <div>
                <Title level={3} className={styles.pageTitle}>
                  {header.name}
                </Title>
                <div className={styles.metaRow}>
                  {header.externalId && (
                    <div className={styles.metaItem}>
                      <TagOutlined className={styles.metaIcon} />
                      <span className={styles.metaLabel}>
                        {t("ProductDetail-Header-SKU")}
                      </span>
                      <span className={styles.metaValue}>{header.externalId}</span>
                    </div>
                  )}
                  {header.brandName && (
                    <div className={styles.metaItem}>
                      <ShopOutlined className={styles.metaIcon} />
                      <span className={styles.metaLabel}>
                        {t("ProductDetail-Header-Brand")}
                      </span>
                      <span className={styles.metaValue}>{header.brandName}</span>
                    </div>
                  )}
                  {header.categoryName && (
                    <div className={styles.metaItem}>
                      <AppstoreOutlined className={styles.metaIcon} />
                      <span className={styles.metaLabel}>
                        {t("ProductDetail-Header-Category")}
                      </span>
                      <span className={styles.metaValue}>
                        {categoryTree(header.categoryName, header.parents)}
                      </span>
                    </div>
                  )}
                  {header.seasons && header.seasons.length > 0 && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        {t("ProductDetail-Header-Season")}
                      </span>
                      <span className={styles.metaValue}>
                        {header.seasons.join(", ")}
                      </span>
                    </div>
                  )}
                  <div className={styles.metaDivider} />
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>
                      {t("ProductDetail-Header-Price")}
                    </span>
                    <span className={styles.metaValuePrice}>{fmt(header.price)}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>
                      {t("ProductDetail-Header-Cost")}
                    </span>
                    <span className={styles.metaValue}>{fmt(header.cost)}</span>
                  </div>
                </div>
              </div>
              <div className={styles.headerRight}>
                {header.alertCount > 0 && (
                  <Tag
                    color="red"
                    icon={<AlertOutlined />}
                    className={styles.alertBadge}
                  >
                    {t("ProductAnalysis-Tag-AlertsCount", {
                      count: header.alertCount,
                    })}
                  </Tag>
                )}
                <div className={styles.headerFilters}>
                  <Select
                    value={period}
                    onChange={setPeriod}
                    className={styles.filterSelect}
                    options={[
                      { value: 30, label: t("ProductAnalysis-Period-30") },
                      { value: 60, label: t("ProductAnalysis-Period-60") },
                      { value: 90, label: t("ProductAnalysis-Period-90") },
                      { value: 180, label: t("ProductAnalysis-Period-180") },
                    ]}
                  />
                  <Select
                    placeholder={t("ProductDetail-Filter-Channel")}
                    allowClear
                    value={channelId}
                    onChange={setChannelId}
                    className={styles.filterSelect}
                    options={channelOptions}
                  />
                  <Select
                    placeholder={t("ProductDetail-Filter-Store")}
                    allowClear
                    value={branchOfficeId}
                    onChange={setBranchOfficeId}
                    className={styles.filterSelect}
                    options={branchOptions}
                  />
                </div>
              </div>
            </div>
          </Card>
        )
      )}

      {kpisLoading ? (
        <ProductDetailKpisSkeleton />
      ) : kpisError ? (
        <ProductDetailSectionError onRetry={fetchKpis} />
      ) : (
        kpis && (
          <div className={styles.kpiRow}>
            {[
              {
                label: t("ProductDetail-Kpi-GrossSales"),
                value: fmt(kpis.grossSales),
                variation: kpis.grossSalesVar,
                icon: <ShoppingCartOutlined />,
              },
              {
                label: t("ProductDetail-Kpi-Units"),
                value: fmtN(kpis.units),
                variation: kpis.unitsVar,
                icon: <TagOutlined />,
              },
              {
                label: t("ProductDetail-Kpi-Margin"),
                value: `${(kpis.marginPct ?? 0).toFixed(1)}%`,
                variation: kpis.marginVar,
                icon: <FlagOutlined />,
                suf: " pp",
              },
              {
                label: t("ProductDetail-Kpi-Stock"),
                value: fmtN(kpis.stock),
                icon: <InboxOutlined />,
              },
              {
                label: t("ProductDetail-Kpi-Turnover"),
                value: (kpis.turnover ?? 0).toFixed(2),
                icon: <SyncOutlined />,
              },
              {
                label: t("ProductDetail-Kpi-PctGlobalSales"),
                value: `${(kpis.pctGlobalSales ?? 0).toFixed(1)}%`,
                icon: <PieChartOutlined />,
              },
            ].map((kpi, i) => (
              <Card className={styles.kpiCard} key={i}>
                <div className={styles.kpiIcon}>{kpi.icon}</div>
                <Text className={styles.kpiLabel}>{kpi.label}</Text>
                <div className={styles.kpiValue}>{kpi.value}</div>
                {kpi.variation !== undefined && (
                  <Vr value={kpi.variation} suffix={kpi.suf || "%"} />
                )}
              </Card>
            ))}
          </div>
        )
      )}

      {chartLoading ? (
        <ProductDetailChartSkeleton />
      ) : chartError ? (
        <ProductDetailSectionError onRetry={fetchChart} />
      ) : (
        <Card
          className={styles.sectionCard}
          title={
            <Space>
              <LineChartOutlined />
              <span>{t("ProductDetail-Section-SalesEvolution")}</span>
            </Space>
          }
        >
          <div className={styles.chartContainer}>
            {data.salesEvolution && data.salesEvolution.length > 0 ? (
              <Line data={chartData} options={chartOpts} />
            ) : (
              <Empty description={t("ProductAnalysis-Empty-NoPeriodData")} />
            )}
          </div>
        </Card>
      )}

      {breakdownLoading ? (
        <ProductDetailBreakdownSkeleton />
      ) : breakdownError ? (
        <ProductDetailSectionError onRetry={fetchBreakdown} />
      ) : (
        <>

        <Card
          className={styles.sectionCard}
          title={
            <Space>
              <ShopOutlined />
              <span>{t("ProductDetail-Section-ChannelPerformance")}</span>
            </Space>
          }
        >
          <Table
            columns={channelCols as any}
            dataSource={(data.byChannel || []).map((r) => ({
              ...r,
              key: r.channelId,
            }))}
            pagination={false}
            size="small"
            className={styles.dataTable}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: (
                <Empty description={t("ProductAnalysis-Empty-NoChannelData")} />
              ),
            }}
          />
        </Card>

        <Card
          className={styles.sectionCard}
          title={
            <Space>
              <ShopOutlined />
              <span>{t("ProductDetail-Section-StorePerformance")}</span>
            </Space>
          }
        >
          <Table
            columns={storeCols as any}
            dataSource={(data.byStore || []).map((r) => ({
              ...r,
              key: r.branchOfficeId,
            }))}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            className={styles.dataTable}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: (
                <Empty description={t("ProductAnalysis-Empty-NoStoreData")} />
              ),
            }}
          />
        </Card>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={8}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <AlertOutlined style={{ color: "#f5222d" }} />
                  <span>{t("ProductDetail-Section-ActiveAlerts")}</span>
                </Space>
              }
            >
              <div className={styles.bulletList}>
                {(data.alerts || []).length === 0 ? (
                  <Text type="secondary">
                    {t("ProductAnalysis-Empty-AlertsPending")}
                  </Text>
                ) : (
                  data.alerts.map((a, i) => (
                    <div key={i} className={styles.bulletItem}>
                      <AlertOutlined className={styles.bulletRed} />
                      <Text>{t(ALERT_I18N_KEYS[a] ?? a)}</Text>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <ThunderboltOutlined style={{ color: "#1890ff" }} />
                  <span>{t("ProductDetail-Section-Drivers")}</span>
                </Space>
              }
            >
              <div className={styles.bulletList}>
                {(data.drivers || []).length === 0 ? (
                  <Text type="secondary">
                    {t("ProductAnalysis-Empty-DriversPending")}
                  </Text>
                ) : (
                  data.drivers.map((d, i) => (
                    <div key={i} className={styles.bulletItem}>
                      <ThunderboltOutlined className={styles.bulletBlue} />
                      <Text>{t(d.key, d.params)}</Text>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <BulbOutlined style={{ color: "#52c41a" }} />
                  <span>{t("ProductDetail-Section-Opportunities")}</span>
                </Space>
              }
            >
              <div className={styles.bulletList}>
                {(data.opportunities || []).length === 0 ? (
                  <Text type="secondary">
                    {t("ProductAnalysis-Empty-OpportunitiesPending")}
                  </Text>
                ) : (
                  data.opportunities.map((o, i) => (
                    <div key={i} className={styles.bulletItem}>
                      <BulbOutlined style={{ color: "#52c41a" }} />
                      <Text>{t(o.key, o.params)}</Text>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>
        </Row>

        <Card
          className={styles.sectionCard}
          title={
            <Space>
              <TrophyOutlined />
              <span>{t("ProductDetail-Section-Promotions")}</span>
            </Space>
          }
        >
          <Table
            columns={[
              {
                title: t("ProductAnalysis-Column-Promotion"),
                dataIndex: "name",
                key: "name",
                sorter: (a: any, b: any) =>
                  (a.name ?? "").localeCompare(b.name ?? ""),
              },
              {
                title: t("ProductAnalysis-Column-Type"),
                dataIndex: "type",
                key: "type",
                render: (v: string) => <Tag color="purple">{v || "—"}</Tag>,
                sorter: (a: any, b: any) =>
                  (a.type ?? "").localeCompare(b.type ?? ""),
              },
              {
                title: t("ProductAnalysis-Column-Uplift"),
                dataIndex: "uplift",
                key: "uplift",
                align: "center" as const,
                render: (v: string | null) => (
                  <Tag color="default">
                    {v || t("ProductAnalysis-Tag-Pending")}
                  </Tag>
                ),
              },
              {
                title: t("ProductAnalysis-Column-ROI"),
                dataIndex: "roi",
                key: "roi",
                align: "center" as const,
                render: (v: string | null) => (
                  <Tag color="default">
                    {v || t("ProductAnalysis-Tag-Pending")}
                  </Tag>
                ),
              },
              {
                title: t("ProductAnalysis-Column-Units"),
                dataIndex: "units",
                key: "units",
                render: (v: number) => fmtN(v),
                align: "right" as const,
                sorter: (a: any, b: any) => (a.units ?? 0) - (b.units ?? 0),
              },
              {
                title: t("ProductAnalysis-Column-Result"),
                dataIndex: "result",
                key: "result",
                render: (v: string | null) => (
                  <Tag color="default">
                    {v || t("ProductAnalysis-Tag-Pending")}
                  </Tag>
                ),
              },
            ]}
            dataSource={(data.promotions || []).map((p) => ({
              ...p,
              key: p.id,
            }))}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            className={styles.dataTable}
            locale={{
              emptyText: (
                <Empty description={t("ProductAnalysis-Empty-NoPromotions")} />
              ),
            }}
          />
        </Card>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={data.similarEnabled ? 12 : 24}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <LinkOutlined />
                  <span>{t("ProductDetail-Section-Complementary")}</span>
                </Space>
              }
            >
              <Table
                columns={[
                  {
                    title: t("ProductAnalysis-Column-Product"),
                    dataIndex: "name",
                    key: "name",
                    render: (v: string, r: any) => (
                      <a
                        onClick={() =>
                          navigate(
                            `/commercial-analysis/products/${r.articleId}`
                          )
                        }
                      >
                        {v}
                      </a>
                    ),
                    sorter: (a: any, b: any) =>
                      (a.name ?? "").localeCompare(b.name ?? ""),
                  },
                  {
                    title: t("ProductAnalysis-Column-Category"),
                    dataIndex: "categoryName",
                    key: "categoryName",
                    render: (v: string) => v || "—",
                    sorter: (a: any, b: any) =>
                      (a.categoryName ?? "").localeCompare(b.categoryName ?? ""),
                  },
                  {
                    title: t("ProductAnalysis-Column-Affinity"),
                    dataIndex: "affinityPct",
                    key: "affinityPct",
                    align: "right" as const,
                    render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
                    sorter: (a: any, b: any) =>
                      (a.affinityPct ?? 0) - (b.affinityPct ?? 0),
                  },
                ]}
                dataSource={(data.complementary || []).map((c) => ({
                  ...c,
                  key: c.articleId,
                }))}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                size="small"
                className={styles.dataTable}
                locale={{
                  emptyText: (
                    <Empty
                      description={t("ProductAnalysis-Empty-NoComplementary")}
                    />
                  ),
                }}
              />
            </Card>
          </Col>
          {data.similarEnabled && (
            <Col xs={24} lg={12}>
              <Card
                className={styles.sectionCard}
                title={
                  <Space>
                    <SwapOutlined />
                    <span>{t("ProductDetail-Section-Similar")}</span>
                  </Space>
                }
              >
                <Table
                  columns={[
                    {
                      title: t("ProductAnalysis-Column-Article"),
                      dataIndex: "name",
                      key: "name",
                    },
                    {
                      title: t("ProductAnalysis-Column-SKU"),
                      dataIndex: "externalId",
                      key: "externalId",
                    },
                    {
                      title: t("ProductAnalysis-Column-Similarity"),
                      dataIndex: "similarityPct",
                      key: "similarityPct",
                      align: "right" as const,
                      render: (v: number) => `${(v ?? 0).toFixed(0)}%`,
                    },
                    {
                      title: t("ProductAnalysis-Column-Reason"),
                      dataIndex: "reason",
                      key: "reason",
                    },
                    {
                      title: t("ProductAnalysis-Column-Stock"),
                      dataIndex: "stock",
                      key: "stock",
                      render: (v: number) => fmtN(v),
                      align: "right" as const,
                    },
                    {
                      title: t("ProductAnalysis-Column-SalesVar"),
                      dataIndex: "grossSalesVar",
                      key: "grossSalesVar",
                      align: "right" as const,
                      render: (v: number) => <Vr value={v} />,
                    },
                  ]}
                  dataSource={(data.similar || []).map((s) => ({
                    ...s,
                    key: s.articleId,
                  }))}
                  pagination={false}
                  size="small"
                  className={styles.dataTable}
                  scroll={{ x: 800 }}
                  locale={{
                    emptyText: (
                      <Empty
                        description={t("ProductAnalysis-Empty-NoSimilar")}
                      />
                    ),
                  }}
                />
              </Card>
            </Col>
          )}
        </Row>

        <Card
          className={styles.sectionCard}
          title={
            <Space>
              <AppstoreOutlined />
              <span>{t("ProductDetail-Section-SkuBreakdown")}</span>
            </Space>
          }
        >
          <Table
            columns={[
              {
                title: t("Common-Name"),
                dataIndex: "name",
                key: "name",
                render: (v: string | null) => v || "—",
                sorter: (a: any, b: any) =>
                  (a.name ?? "").localeCompare(b.name ?? ""),
              },
              {
                title: t("ProductAnalysis-Column-Size"),
                dataIndex: "size",
                key: "size",
                render: (v: string | null) => v || "—",
                sorter: (a: any, b: any) =>
                  (a.size ?? "").localeCompare(b.size ?? ""),
              },
              {
                title: t("ProductAnalysis-Column-Color"),
                dataIndex: "color",
                key: "color",
                render: (v: string | null) => v || "—",
                sorter: (a: any, b: any) =>
                  (a.color ?? "").localeCompare(b.color ?? ""),
              },
              {
                title: t("ProductAnalysis-Column-Units"),
                dataIndex: "units",
                key: "units",
                align: "right" as const,
                render: (v: number) => fmtN(v),
                sorter: (a: any, b: any) => (a.units ?? 0) - (b.units ?? 0),
              },
              {
                title: t("ProductAnalysis-Column-PctOfArticle"),
                dataIndex: "pctOfArticle",
                key: "pctOfArticle",
                align: "right" as const,
                render: (v: number) => `${(v ?? 0).toFixed(1)}%`,
                sorter: (a: any, b: any) =>
                  (a.pctOfArticle ?? 0) - (b.pctOfArticle ?? 0),
              },
              {
                title: t("ProductAnalysis-Column-Stock"),
                dataIndex: "stock",
                key: "stock",
                align: "right" as const,
                render: (v: number) => fmtN(v),
                sorter: (a: any, b: any) => (a.stock ?? 0) - (b.stock ?? 0),
              },
              {
                title: t("ProductAnalysis-Column-Turnover"),
                dataIndex: "turnover",
                key: "turnover",
                align: "right" as const,
                render: (v: number) => (v ?? 0).toFixed(2),
                sorter: (a: any, b: any) => (a.turnover ?? 0) - (b.turnover ?? 0),
              },
            ]}
            dataSource={(data.skus || []).map((s) => ({ ...s, key: s.skuId }))}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            className={styles.dataTable}
            scroll={{ x: 1100 }}
            locale={{
              emptyText: (
                <Empty description={t("ProductAnalysis-Empty-NoSkus")} />
              ),
            }}
          />
        </Card>
        </>
      )}
    </div>
  );
};

export default ProductDetail;
